import type { ElasticVehicleMedia } from '@//app/api/search/vehicles/[id]';
import { type CfitImage, urlToCfit } from '@//utils/image/urlToCfit';

interface GalleryMediaBase {
  type: 'IMAGE' | 'VIDEO' | 'SPHERE';
  index: number;
  caption?: string;
}

type GalleryEmbeddedType = 'VIDEO' | 'YOUTUBE' | 'VIMEO';

export interface GalleryEmbedded extends GalleryMediaBase {
  type: 'VIDEO';
  url: string;
  embeddedType: GalleryEmbeddedType;
  alt: string;
}

export interface GalleryImage extends GalleryMediaBase {
  type: 'IMAGE';
  src: string;
  srcSet?: string;
  webpSrc?: string;
  webpSrcSet?: string;
  alt: string;
  width?: number;
  height?: number;
}

export interface GallerySphere extends GalleryMediaBase {
  type: 'SPHERE';
  src: string;
  srcSet?: string;
  alt: string;
  width?: number;
  height?: number;
}

// ---- Fallback ----

const assetPrefix = process.env.NEXT_PUBLIC_ASSETS_CDN || '';

export const fallbackGalleryImage: CfitImage = {
  src: `${assetPrefix}/images/placeholders/product-gallery.jpg`,
  srcSet: `${assetPrefix}/images/placeholders/product-gallery@2x.jpg 2x`,
  alt: 'Image missing',
  width: 450,
  height: 299,
};

// ---- Embedded Resolver ----

const resolveEmbeddedType = (src: string): GalleryEmbeddedType => {
  if (/youtube\.com\/watch\?v=/.test(src)) return 'YOUTUBE';
  if (/vimeo\.com\/(?:video\/)?\d+/.test(src)) return 'VIMEO';
  return 'VIDEO';
};

// ---- Transformers ----

const toEmbedded = (
  media: ElasticVehicleMedia,
  vehicleTitle: string,
  index: number,
): GalleryEmbedded | undefined => {
  const url = media.files?.[0]?.url;
  if (!url) return undefined;
  return {
    type: 'VIDEO',
    url,
    embeddedType: resolveEmbeddedType(url),
    alt: `Video of ${vehicleTitle}`,
    index,
    caption: media.description ?? undefined,
  };
};

const toImage = (media: ElasticVehicleMedia, vehicleTitle: string, index: number): GalleryImage => {
  const url = media.files?.[0]?.url;
  if (!url) {
    return {
      type: 'IMAGE',
      src: fallbackGalleryImage.src,
      srcSet: fallbackGalleryImage.srcSet,
      alt: media.description ?? fallbackGalleryImage.alt,
      caption: media.description ?? undefined,
      width: fallbackGalleryImage.width,
      height: fallbackGalleryImage.height,
      index,
    };
  }

  const imageOptions = {
    width: 1170 as const,
    alt: `Image of ${vehicleTitle}`,
  };

  const jpegImage = urlToCfit(url, {
    ...imageOptions,
    format: 'jpeg',
  });

  const webpImage = urlToCfit(url, {
    ...imageOptions,
    format: 'webp',
  });

  return {
    type: 'IMAGE',
    src: jpegImage.src,
    srcSet: jpegImage.srcSet,
    webpSrc: webpImage.src,
    webpSrcSet: webpImage.srcSet,
    alt: media.description ?? jpegImage.alt,
    caption: media.description ?? undefined,
    width: jpegImage.width,
    height: jpegImage.height,
    index,
  };
};

const to360 = (
  media: ElasticVehicleMedia,
  vehicleTitle: string,
  baseIndex: number,
): GalleryImage[] =>
  (media.files || []).map((file, idx) => {
    const imageOptions = {
      width: 1170 as const,
      alt: `360 view of ${vehicleTitle} ${idx + 1}`,
    };

    const jpegImage = urlToCfit(file.url, {
      ...imageOptions,
      format: 'jpeg',
    });

    const webpImage = urlToCfit(file.url, {
      ...imageOptions,
      format: 'webp',
    });

    return {
      type: 'IMAGE' as const,
      src: jpegImage.src,
      srcSet: jpegImage.srcSet,
      webpSrc: webpImage.src,
      webpSrcSet: webpImage.srcSet,
      alt: jpegImage.alt,
      width: jpegImage.width,
      height: jpegImage.height,
      index: baseIndex + idx,
      caption: media.description ?? undefined,
    };
  });

const toSphere = (
  media: ElasticVehicleMedia,
  vehicleTitle: string,
  index: number,
): GallerySphere | undefined => {
  const url = media.files?.[0]?.url;
  if (!url) {
    return {
      type: 'SPHERE',
      src: fallbackGalleryImage.src,
      srcSet: fallbackGalleryImage.srcSet,
      alt: fallbackGalleryImage.alt,
      width: fallbackGalleryImage.width,
      height: fallbackGalleryImage.height,
      index,
      caption: media.description ?? undefined,
    };
  }

  const image = urlToCfit(url, {
    alt: `Image of ${vehicleTitle}`,
    format: 'jpeg',
  });

  return {
    type: 'SPHERE',
    src: image.src,
    srcSet: image.srcSet,
    alt: image.alt,
    width: image.width,
    height: image.height,
    index,
    caption: media.description ?? undefined,
  };
};

// ---- Main Transformer ----

interface TransformMediaResult {
  media: (GalleryImage | GalleryEmbedded | GallerySphere)[];
  videos: GalleryEmbedded[];
  images: GalleryImage[];
  spheres: GallerySphere[];
  '360': GalleryImage[][];
  statistics: {
    images: number;
    videos: number;
    spheres: number;
    '360': number;
  };
}

export const transformMedia = (
  media: ElasticVehicleMedia[] | undefined,
  title: string,
): TransformMediaResult => {
  if (!media) {
    return {
      media: [],
      videos: [],
      images: [],
      spheres: [],
      '360': [],
      statistics: {
        images: 0,
        videos: 0,
        spheres: 0,
        '360': 0,
      },
    };
  }

  const result: {
    media: (GalleryImage | GalleryEmbedded | GallerySphere)[];
    videos: GalleryEmbedded[];
    images: GalleryImage[];
    spheres: GallerySphere[];
    '360': GalleryImage[][];
  } = {
    media: [],
    videos: [],
    images: [],
    spheres: [],
    '360': [],
  };

  media.forEach((item, idx) => {
    if (item.type === 'Image') {
      if (item.files.length === 1) {
        const img = toImage(item, title, idx);
        result.media.push(img);
        result.images.push(img);
      } else if (item.files.length > 1) {
        const imgs = to360(item, title, idx * 100);
        result.media.push(...imgs);
        result['360'].push(imgs);
      }
    } else if (item.type === 'Embedded') {
      const vid = toEmbedded(item, title, idx);
      if (vid) {
        result.media.push(vid);
        result.videos.push(vid);
      }
    } else if (item.type === 'Sphere') {
      const sph = toSphere(item, title, idx);
      if (sph) {
        result.media.push(sph);
        result.spheres.push(sph);
      }
    }
  });

  return {
    ...result,
    statistics: {
      images: result.images.length,
      '360': result['360'].length,
      videos: result.videos.length,
      spheres: result.spheres.length,
    },
  };
};
